import type { WidgetConfig } from './types.js';

const DEFAULT_ACCENT = '#0b5fff'; // 5.1:1 on white — passes WCAG 1.4.3 for UI text

const POSITIONS = new Set([
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
  // Logical. Resolved against reading direction at layout time, not here — see
  // `placement()` in widget.ts. Purely additive: the physical values above keep
  // working exactly as before, so no existing install changes.
  'bottom-start',
  'bottom-end',
  'top-start',
  'top-end',
]);

/**
 * The tag that loaded us, captured at module evaluation.
 *
 * This runs during our own synchronous execution, which is the only moment
 * `document.currentScript` is guaranteed to point at us — and it does so for
 * `defer` and `async` installs too, not just inline parsing. A previous version
 * of this file claimed otherwise and fell back to finding the script by
 * matching its `src` against a regex; that was both unnecessary and unsafe.
 *
 * Why unsafe: the regex was unanchored and took the *last* match in the
 * document, so any `<script src>` whose URL merely contained "shakuf.js"
 * became the config source. An attacker with HTML injection (no script
 * execution needed) could plant such an element — CSP blocking the load leaves
 * the element sitting in the DOM with its `data-*` attributes readable — and
 * thereby control the accessibility coordinator's name, phone and email shown
 * in our panel, which visitors reasonably read as first-party site chrome.
 *
 * Reading `currentScript` at module top level removes the heuristic entirely.
 * It is null on the npm/bundler path, where callers pass config to `mount()`
 * directly, so nothing is lost there.
 *
 * The `typeof document` guard is what keeps the npm entry importable outside a
 * browser. Without it, `import '@shakuf-widget/widget'` threw a ReferenceError
 * at evaluation under Next/Nuxt/Remix SSR and Node test runners, before anyone
 * had called `mount()` — contradicting the promise in index.ts that importing
 * does nothing on its own.
 */
const OWN_SCRIPT: HTMLScriptElement | null =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

function str(el: Element | null, name: string): string | null {
  const v = el?.getAttribute(`data-${name}`);
  if (v == null) return null;
  const trimmed = v.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Only http(s), mailto and tel, plus same-origin relative paths.
 *
 * The site owner supplies these, so this is not a trust boundary — it is a
 * guard against a stray `javascript:` value turning a config typo into an
 * injection on their own page.
 */
function safeUrl(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, document.baseURI);
    if (['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) {
      return url.href;
    }
  } catch {
    /* fall through */
  }
  return null;
}

/** Rejects anything that isn't a plain hex colour. */
function safeColor(raw: string | null): string | null {
  return raw && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw) ? raw : null;
}

/**
 * A CSS selector that is safe to interpolate into our stylesheet text.
 *
 * Unlike `data-mount`, this value does not go to `querySelector` at runtime —
 * it is concatenated into a `<style>` we write onto the host page. So a value
 * like `x} body{display:none} .y{` would close our rule and open one of its
 * own: CSS injection on the site owner's own page. Braces are rejected
 * explicitly, and `querySelector` is then used purely as a parser (it raises
 * SyntaxError rather than returning null, the same idiom as `resolveMount`),
 * which also rejects comment openers and other malformed input.
 *
 * Rejection warns and falls back to "no exclusion", which is the previous
 * behaviour rather than a broken sheet.
 */
export function safeSelector(raw: string | null, attr: string): string | null {
  if (!raw) return null;
  if (/[{}]/.test(raw)) {
    warn(`data-${attr} may not contain braces: ${raw}`);
    return null;
  }
  try {
    document.querySelector(raw);
    return raw;
  } catch {
    warn(`data-${attr} is not a valid CSS selector: ${raw}`);
    return null;
  }
}

const DEFAULT_OFFSET = 20;

/**
 * Resolves `data-mount` to an element, or null to mean `<body>`.
 *
 * Two ways this legitimately fails, and both fall back rather than throw: an
 * invalid selector (`querySelector` raises SyntaxError, it does not return
 * null), and a valid selector matching nothing yet — the host's root may mount
 * after us. Falling back keeps the widget present, which is the right call for
 * an accessibility tool.
 *
 * It warns on the way, because a silent fall back to `<body>` reproduces the
 * exact bug `data-mount` exists to fix, invisibly. The installer needs to know
 * their selector missed.
 */
export function resolveMount(selector: string | null): HTMLElement {
  if (!selector) return document.body;

  let found: Element | null = null;
  try {
    found = document.querySelector(selector);
  } catch {
    warn(`data-mount is not a valid CSS selector: ${selector}`);
    return document.body;
  }

  if (found instanceof HTMLElement) return found;

  warn(`data-mount matched no element: ${selector}. Falling back to <body>.`);
  return document.body;
}

function warn(message: string): void {
  if (typeof console !== 'undefined') console.warn(`[shakuf] ${message}`);
}

/**
 * Says so when there was no script tag to read.
 *
 * `document.currentScript` is null for `type="module"` scripts and under some
 * CMS and tag-manager loaders that inject the tag through `innerHTML` or
 * `eval`. On those installs every `data-*` option was dropped without a word:
 * the widget booted with no statement link, no coordinator and the default
 * corner, and the installer had nothing to tell them why. Called from the
 * script-tag entry only — bundler installs pass options to `mount()` and never
 * had a tag to read, so for them silence is correct.
 */
export function warnIfScriptUnknown(): void {
  if (OWN_SCRIPT) return;
  warn(
    'document.currentScript is unavailable, so data-* options on the script tag are ignored. Load the widget with a classic <script defer> tag, or pass options to mount().',
  );
}

/**
 * Parses a numeric attribute, falling back when absent or out of range.
 *
 * The explicit null check is the point. `Number(null)` is `0`, and `0` passes
 * an `isFinite && >= 0` guard cleanly — so reading the attribute straight into
 * `Number()` silently produced an offset of 0 on every install that did not set
 * one, pinning the launcher flush into the viewport corner instead of the
 * documented 20px. The absent case has to be handled before coercion, not after.
 */
function num(el: Element | null, name: string, fallback: number, max: number): number {
  const raw = str(el, name);
  if (raw === null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= max ? n : fallback;
}

export function readConfig(): WidgetConfig {
  const el = OWN_SCRIPT;
  const position = str(el, 'position');

  return {
    position:
      position && POSITIONS.has(position)
        ? (position as WidgetConfig['position'])
        : 'bottom-right',
    offset: num(el, 'offset', DEFAULT_OFFSET, 200),
    statementUrl: safeUrl(str(el, 'statement-url')),
    coordinatorName: str(el, 'coordinator-name'),
    coordinatorPhone: str(el, 'coordinator-phone'),
    coordinatorEmail: str(el, 'coordinator-email'),
    accent: safeColor(str(el, 'accent')) ?? DEFAULT_ACCENT,
    byUrl: safeUrl(str(el, 'by-url')),
    byName: str(el, 'by-name'),
    mount: str(el, 'mount'),
    motionExclude: safeSelector(str(el, 'motion-exclude'), 'motion-exclude'),
    lang: str(el, 'lang'),
    // Presence-or-value, the way HTML booleans normally work: `data-hidden`,
    // `data-hidden=""` and `data-hidden="true"` all mean hidden. Only an
    // explicit "false" opts back out, so a host templating the attribute in
    // can write the value straight from a boolean without a special case.
    hidden: el?.hasAttribute('data-hidden') === true && str(el, 'hidden') !== 'false',
    // Objects cannot travel on a data attribute. Bundler hosts pass this to
    // `mount()`; on the script-tag path it is always absent.
    initialPrefs: null,
  };
}
